/**
 * Normalize API / Axios / RTK errors into a serializable frontend error.
 * Never throws; never embeds Axios config / secrets.
 */

import { ERROR_KIND, OUTCOME, hasMeaningfulErrors } from "./types";

const MAX_MESSAGE_LEN = 2000;
const MAX_FIELD_ERRORS = 50;

function clip(str, max = MAX_MESSAGE_LEN) {
  if (str == null) return "";
  const s = typeof str === "string" ? str : String(str);
  return s.length > max ? `${s.slice(0, max)}…` : s;
}

function safeFieldErrors(raw) {
  if (!hasMeaningfulErrors(raw)) return [];
  const out = [];
  if (Array.isArray(raw)) {
    raw.forEach((item, i) => {
      if (out.length >= MAX_FIELD_ERRORS) return;
      if (typeof item === "string") {
        out.push({ path: "base", code: "error", message: clip(item) });
      } else if (item && typeof item === "object") {
        out.push({
          path: clip(item.path || item.source?.pointer || "base", 200),
          code: clip(item.code || "error", 80),
          message: clip(item.message || item.detail || item.title || ""),
          rowKey: item.rowKey || item.row_key || null,
        });
      } else {
        out.push({ path: "base", code: "error", message: clip(String(item)) });
      }
    });
    return out;
  }
  if (typeof raw === "object") {
    Object.entries(raw).forEach(([field, messages]) => {
      const list = Array.isArray(messages) ? messages : [messages];
      list.forEach((msg) => {
        if (out.length >= MAX_FIELD_ERRORS) return;
        out.push({
          path: clip(field, 200),
          code: "invalid",
          message: clip(
            typeof msg === "string"
              ? msg
              : msg?.message || msg?.detail || String(msg)
          ),
        });
      });
    });
  }
  return out;
}

function kindFromStatus(status) {
  if (status == null) return ERROR_KIND.UNKNOWN;
  if (status === 401) return ERROR_KIND.AUTHENTICATION;
  if (status === 403) return ERROR_KIND.AUTHORIZATION;
  if (status === 404) return ERROR_KIND.NOT_FOUND;
  if (status === 409) return ERROR_KIND.CONFLICT;
  if (status === 422 || status === 400) return ERROR_KIND.VALIDATION;
  if (status === 429) return ERROR_KIND.RATE_LIMIT;
  if (status >= 500) return ERROR_KIND.SERVER;
  return ERROR_KIND.UNKNOWN;
}

function emptyNormalized(partial = {}) {
  return {
    kind: ERROR_KIND.UNKNOWN,
    code: "UNKNOWN",
    safeMessage: "En uventet feil oppstod.",
    messageKey: null,
    fieldErrors: [],
    httpStatus: null,
    requestId: null,
    clientIncidentId: null,
    operationKey: null,
    outcome: OUTCOME.UNKNOWN,
    recovery: "none",
    retryAfter: null,
    alreadyNormalized: true,
    ...partial,
  };
}

/**
 * @param {unknown} input
 * @param {object} [context]
 */
export function normalizeError(input, context = {}) {
  try {
    if (input && typeof input === "object" && input.alreadyNormalized) {
      return { ...input, ...context };
    }

    // Cancelled
    if (
      input?.code === "ERR_CANCELED" ||
      input?.name === "CanceledError" ||
      input?.name === "AbortError" ||
      (typeof input?.message === "string" &&
        /cancel|abort/i.test(input.message) &&
        !input?.response)
    ) {
      return emptyNormalized({
        kind: ERROR_KIND.CANCELLED,
        code: "CANCELLED",
        safeMessage: "Forespørselen ble avbrutt.",
        outcome: OUTCOME.UNKNOWN,
        recovery: "none",
        operationKey: context.operationKey || null,
        clientIncidentId: context.clientIncidentId || null,
      });
    }

    // Axios-like
    const response = input?.response;
    const status = response?.status ?? input?.httpStatus ?? null;
    const data = response?.data ?? input?.data ?? input?.payload ?? input;
    const headers = response?.headers || {};
    const requestId =
      headers["x-request-id"] ||
      headers["X-Request-Id"] ||
      data?.problem?.request_id ||
      data?.request_id ||
      null;

    if (data?.problem && typeof data.problem === "object") {
      const p = data.problem;
      return emptyNormalized({
        kind: p.kind || kindFromStatus(status),
        code: clip(p.code || "PROBLEM", 80),
        safeMessage: clip(
          p.message || "Handlingen kunne ikke fullføres."
        ),
        messageKey: p.message_key || p.messageKey || null,
        fieldErrors: safeFieldErrors(p.field_errors || p.fieldErrors),
        httpStatus: status,
        requestId: p.request_id || requestId,
        outcome: p.outcome || OUTCOME.UNKNOWN,
        recovery: p.recovery || "edit",
        retryAfter: p.retry_after || p.retryAfter || null,
        operationKey: context.operationKey || null,
        clientIncidentId: context.clientIncidentId || null,
      });
    }

    // Network / timeout without response
    if (input?.isAxiosError && !response) {
      const isTimeout =
        input.code === "ECONNABORTED" || /timeout/i.test(input.message || "");
      return emptyNormalized({
        kind: isTimeout ? ERROR_KIND.TIMEOUT : ERROR_KIND.NETWORK,
        code: isTimeout ? "TIMEOUT" : "NETWORK",
        safeMessage: isTimeout
          ? "Forespørselen tok for lang tid. Vi kunne ikke bekrefte resultatet."
          : "Nettverksfeil. Vi kunne ikke bekrefte resultatet.",
        httpStatus: null,
        outcome: OUTCOME.UNKNOWN,
        recovery: "check_status",
        operationKey: context.operationKey || null,
        clientIncidentId: context.clientIncidentId || null,
      });
    }

    // RTK rejectWithValue payload / body
    const errors = data?.errors;
    // Bare string `errors` (agreements create_report etc.)
    if (typeof errors === "string" && errors.trim()) {
      return emptyNormalized({
        kind: kindFromStatus(status) || ERROR_KIND.VALIDATION,
        code:
          status === 422
            ? "VALIDATION_FAILED"
            : clip(String(data?.code || "REQUEST_FAILED"), 80),
        safeMessage: clip(errors),
        fieldErrors: [{ path: "base", code: "error", message: clip(errors) }],
        httpStatus: status,
        requestId,
        outcome:
          status === 422 || status === 400
            ? OUTCOME.NOT_APPLIED
            : OUTCOME.UNKNOWN,
        recovery: "edit",
        operationKey: context.operationKey || null,
        clientIncidentId: context.clientIncidentId || null,
      });
    }

    const fieldErrors = safeFieldErrors(errors);
    if (hasMeaningfulErrors(errors) || data?.error || data?.message) {
      const msg =
        (typeof data?.message === "string" && data.message) ||
        (typeof data?.error === "string" && data.error) ||
        fieldErrors[0]?.message ||
        "Handlingen kunne ikke fullføres.";
      return emptyNormalized({
        kind: kindFromStatus(status) || ERROR_KIND.VALIDATION,
        code:
          status === 422
            ? "VALIDATION_FAILED"
            : clip(String(data?.code || "REQUEST_FAILED"), 80),
        safeMessage: clip(msg),
        fieldErrors,
        httpStatus: status,
        requestId,
        outcome:
          status === 422 || status === 400
            ? OUTCOME.NOT_APPLIED
            : OUTCOME.UNKNOWN,
        recovery: fieldErrors.length ? "edit" : "reload",
        operationKey: context.operationKey || null,
        clientIncidentId: context.clientIncidentId || null,
      });
    }

    // JSON:API errors
    if (Array.isArray(data?.errors) === false && Array.isArray(data)) {
      /* fall through */
    }
    if (data?.errors && Array.isArray(data.errors) && data.errors[0]?.detail) {
      return emptyNormalized({
        kind: kindFromStatus(status),
        code: clip(data.errors[0].code || "JSONAPI_ERROR", 80),
        safeMessage: clip(data.errors[0].detail || data.errors[0].title),
        fieldErrors: safeFieldErrors(data.errors),
        httpStatus: status,
        requestId,
        outcome: OUTCOME.UNKNOWN,
        recovery: "reload",
        operationKey: context.operationKey || null,
      });
    }

    if (typeof input === "string") {
      return emptyNormalized({
        kind: ERROR_KIND.UNKNOWN,
        code: "STRING_ERROR",
        safeMessage: clip(input),
        outcome: OUTCOME.UNKNOWN,
        operationKey: context.operationKey || null,
      });
    }

    if (input instanceof Error) {
      return emptyNormalized({
        kind: ERROR_KIND.UNKNOWN,
        code: "ERROR",
        safeMessage: clip(input.message || "En uventet feil oppstod."),
        outcome: OUTCOME.UNKNOWN,
        operationKey: context.operationKey || null,
      });
    }

    return emptyNormalized({
      kind: kindFromStatus(status),
      code: "UNKNOWN",
      safeMessage: "En uventet feil oppstod.",
      httpStatus: status,
      requestId,
      operationKey: context.operationKey || null,
      clientIncidentId: context.clientIncidentId || null,
    });
  } catch (_) {
    return emptyNormalized({
      kind: ERROR_KIND.UNKNOWN,
      code: "NORMALIZE_FAILED",
      safeMessage: "En uventet feil oppstod.",
      operationKey: context.operationKey || null,
    });
  }
}

/**
 * Classify HTTP success-ish responses that are not failures.
 * @param {number|null} status
 * @param {unknown} body
 */
export function classifyHttpSuccess(status, body) {
  if (status === 204) {
    // FA-07: 204 short-circuits before body.errors — confusing payloads must
    // not be 204+errors; prefer 404/problem at the API.
    return { ok: true, outcome: OUTCOME.APPLIED, note: "no_content" };
  }
  if (status === 202) {
    return { ok: true, outcome: OUTCOME.UNKNOWN, note: "accepted_pending" };
  }
  // FA-08: `{ error: true }` @ 2xx must not classify as success.
  if (body && body.error === true) {
    return { ok: false, outcome: OUTCOME.NOT_APPLIED, note: "body_error_flag" };
  }
  if (body && hasMeaningfulErrors(body.errors)) {
    // FA-05: global default treats meaningful errors as failure. Endpoints
    // with legitimate batch warnings must pass an endpoint-specific adapter
    // that strips/relabels warnings before calling this helper.
    return { ok: false, outcome: OUTCOME.NOT_APPLIED, note: "body_errors" };
  }
  // Empty errors collection is not failure
  if (body && body.errors != null && !hasMeaningfulErrors(body.errors)) {
    return { ok: true, outcome: OUTCOME.APPLIED, note: "empty_errors" };
  }
  return { ok: true, outcome: OUTCOME.APPLIED, note: "ok" };
}
